// Types related to Festivals, Stages, Sets, Artists

/**
 * Basic Stage information
 * All stages are canonical by default - they represent the physical stage itself
 */
export interface Stage {
	id: string;
	name: string;
	festival_brand: string; // Which festival family owns this stage
	stage_genre?: string | null; // Genre/type of music (main, electronic, house, trance, etc.)
	stage_status?: "active" | "retired" | null;
	position?: number | null; // Display order
	description?: string | null;
	capacity?: number | null;
	image_url?: string | null;
	created_at?: string;
	updated_at?: string;
}

/**
 * Stage with additional metadata (for future use)
 */
export interface StageWithMetadata extends Stage {
	total_sets?: number;
	unique_artists?: number;
	years_active?: number[];
}

/**
 * Festival Event Type Information
 * Returned by get_festival_event_types() Postgres function
 */
export interface FestivalEventType {
	event_id: string;
	event_type: "main_festival" | "pre_party" | "after_party";
	event_name: string;
	event_source: "set" | "show";
	event_count: number;
	view_type: "integrated" | "show_list";
}

/**
 * Basic Festival information used in listing cards.
 */
export interface Festival {
	id: string;
	name: string;
	slug: string;
	location: string;
	venue_obj?: {
		id: string;
		name: string;
		slug: string;
		city: string;
		state?: string | null;
		country: string;
		venue_type?: string | null;
		capacity?: number | null;
	} | null;
	description: string | null;
	start_date: string; // ISO Date string
	end_date: string; // ISO Date string
	poster_url: string | null; // CDN URL for festival poster (Supabase storage)
	map_urls: string[] | null; // CDN URLs for festival maps (stages, logistics, etc.)
	alias?: string | null; // Alternative name for improved search (e.g., "Electric Daisy Carnival" when name is "EDC")
	year?: number | null; // Festival year (2020-2030)
	festival_family_slug?: string | null; // Festival family identifier (e.g., "edc", "coachella", "ultra")
	schedule_start_hour?: number; // Hour (0-23) to start displaying schedule
	schedule_end_hour?: number; // Hour (0-23) to end displaying schedule
	schedule_overnight?: boolean; // Whether the festival runs overnight
	schedule_day_start_hour?: number; // Hour when the festival "day" starts
	avg_overall_rating?: number; // Average overall rating (0-10)
	avg_vibe_rating?: number; // Average vibe rating (0-10)
	avg_food_rating?: number; // Average food rating (0-10)
	avg_logistics_rating?: number; // Average logistics rating (0-10)
	avg_production_rating?: number; // Average production rating (0-10)
	review_count?: number; // Number of reviews
	schedule_status?: "lineup_only" | "complete"; // Indicates if festival has full schedule or just lineup
	is_cancelled?: boolean; // Indicates if the festival has been cancelled
	store_url?: string | null; // URL to festival merchandise store
	website_url?: string | null; // URL to official festival website
	playlist_url?: string | null; // URL to official festival playlist (e.g., Spotify)
	youtube_url?: string | null; // URL to official festival YouTube channel
	ticket_url?: string | null; // URL to buy festival tickets
	instagram_url?: string | null; // URL to official festival Instagram
	facebook_url?: string | null; // URL to official festival Facebook
	twitter_url?: string | null; // URL to official festival Twitter
	tiktok_url?: string | null; // URL to official festival TikTok
	spotify_url?: string | null; // URL to official festival Spotify profile
	pre_party_name?: string | null; // Custom label for pre-party events (falls back to "Pre-Party")
	after_party_name?: string | null; // Custom label for after-party events (falls back to "Official After-Party")
	timezone?: string | null; // IANA timezone identifier (e.g., "America/Los_Angeles", "Europe/Paris")
}

/**
 * Detailed Festival information used on the specific festival page.
 */
export interface FestivalDetails {
	id: string;
	name: string;
	slug: string;
	location: string;
	venue_obj?: {
		id: string;
		name: string;
		slug: string;
		city: string;
		state?: string | null;
		country: string;
		venue_type?: string | null;
		capacity?: number | null;
		address?: string | null;
		description?: string | null;
	} | null;
	description: string | null;
	start_date: string; // ISO Date string
	end_date: string; // ISO Date string
	poster_url: string | null; // CDN URL for festival poster (Supabase storage)
	map_urls: string[] | null; // CDN URLs for festival maps (stages, logistics, etc.)
	alias?: string | null; // Alternative name for improved search (e.g., "Electric Daisy Carnival" when name is "EDC")
	year?: number | null; // Festival year (2020-2030)
	festival_family_slug?: string | null; // Festival family identifier (e.g., "edc", "coachella", "ultra")
	schedule_start_hour?: number; // Hour (0-23) to start displaying schedule
	schedule_end_hour?: number; // Hour (0-23) to end displaying schedule
	schedule_overnight?: boolean; // Whether the festival runs overnight
	schedule_day_start_hour?: number; // Hour when the festival "day" starts
	avg_overall_rating?: number; // Average overall rating (0-10)
	avg_vibe_rating?: number; // Average vibe rating (0-10)
	avg_food_rating?: number; // Average food rating (0-10)
	avg_logistics_rating?: number; // Average logistics rating (0-10)
	avg_production_rating?: number; // Average production rating (0-10)
	review_count?: number; // Number of reviews
	schedule_status?: "lineup_only" | "complete"; // Indicates if festival has full schedule or just lineup
	is_cancelled?: boolean; // Indicates if the festival has been cancelled
	store_url?: string | null; // URL to festival merchandise store
	website_url?: string | null; // URL to official festival website
	playlist_url?: string | null; // URL to official festival playlist (e.g., Spotify)
	youtube_url?: string | null; // URL to official festival YouTube channel
	ticket_url?: string | null; // URL to buy festival tickets
	instagram_url?: string | null; // URL to official festival Instagram
	facebook_url?: string | null; // URL to official festival Facebook
	twitter_url?: string | null; // URL to official festival Twitter
	tiktok_url?: string | null; // URL to official festival TikTok
	spotify_url?: string | null; // URL to official festival Spotify profile
	pre_party_name?: string | null; // Custom label for pre-party events (falls back to "Pre-Party")
	after_party_name?: string | null; // Custom label for after-party events (falls back to "Official After-Party")
	production?: string | null; // Production company name
	production_url?: string | null; // URL to production company website
	timezone?: string | null; // IANA timezone identifier (e.g., "America/Los_Angeles", "Europe/Paris")
}

/**
 * Set artist relationship from the set_artists junction table
 */
export interface SetArtist {
	id: string;
	set_id: string;
	artist_id: string;
	position: number;
	alias_name?: string | null; // Artist-specific alias for this set (e.g., "HI-LO" for Oliver Heldens)
	artist: {
		id: string;
		name: string;
		slug?: string;
		image_url: string | null;
		spotify_id?: string | null;
		spotify_popularity?: number | null;
		spotify_genres: string[] | null;
		manual_genres?: string[] | null;
		aliases?: { alias_name: string }[];
	};
}

/**
 * Program Set data structure for non-artist festival programming
 */
export interface ProgramSetData {
	name: string;
	program_type: string;
	registration_required: boolean;
	description: string | null;
}

/**
 * Raw Set data structure as fetched from Supabase,
 * including joined Artist and Stage details.
 * Now supports both single artist (legacy) and B2B sets.
 * Also supports TBA/surprise artists.
 */
export interface FetchedDbSet {
	id: string;
	start_time: string; // ISO 8601 timestamp string
	end_time: string; // ISO 8601 timestamp string
	notes: string | null;
	youtube_url: string | null; // YouTube URL for stream of this set
	soundcloud_url?: string | null; // SoundCloud URL for recording of this set
	apple_music_url?: string | null; // Apple Music URL for recording of this set
	alias_name: string | null; // The alias name to display for this specific performance
	is_group_alias?: boolean; // Whether the alias_name refers to a group (e.g., "Everything Always") vs individual artist
	group_alias_slug?: string; // The slug to use for group routing when is_group_alias is true
	group_alias_name?: string | null; // The display name of the group alias (e.g., "Everything Always")
	member_artists?: Array<{
		artist_id: string;
		display_order: number;
		artist_name: string;
		artist_image_url?: string | null;
		artist_spotify_id?: string | null;
		artist_spotify_genres?: string[] | null;
		artist_manual_genres?: string[] | null;
		artist_spotify_url?: string | null;
		artist_youtube_url?: string | null;
		artist_instagram?: string | null;
		artist_twitter?: string | null;
		artist_slug?: string;
		artist_website_url?: string | null;
	}>; // Member artists for group aliases
	presents_name?: string | null; // Special presentation name (e.g., "Face II Face A/V" for themed sets)
	friends_set?: boolean; // Whether this is an "Artist & Friends" set
	is_collaborative?: boolean; // Whether this is a B2B/collaborative set
	display_format?: "B2B" | "x" | "+" | "F2F" | "&" | "vs" | "B3B" | "with"; // How to display the collaboration
	is_surprise?: boolean; // Whether this was originally TBA/mystery and should preserve surprise display
	placeholder_name?: string | null; // Display name for TBA/mystery slots (e.g., "?????", "TBA", "Special Guest")
	special_guests?: string | null; // Comma-separated list of special guests for this set
	event_type?: "main_festival" | "pre_party" | "after_party"; // Event type for filtering same-venue pre/after parties
	artist: {
		id: string;
		name: string;
		slug?: string;
		image_url: string | null;
		spotify_id?: string | null;
		spotify_popularity?: number | null;
		spotify_genres: string[] | null;
		manual_genres?: string[] | null;
		aliases?: { alias_name: string }[];
	} | null; // Artist can be null for TBA sets
	stage: {
		id: string;
		name: string;
	} | null; // Stage can be null for lineup-only festivals
	// For B2B sets: array of artists in order
	set_artists?: SetArtist[];
	// Review aggregation data
	avg_rating?: number;
	review_count?: number;
	loved_count?: number;
}

/**
 * Processed Set data structure used for rendering in the UI,
 * including calculated day, time formatting, and duration.
 * Now supports both single artist (legacy) and B2B sets.
 * Also supports TBA/surprise artists.
 */
export interface ProcessedSet {
	id: string;
	artist: {
		id: string;
		name: string;
		slug?: string;
		image_url?: string | null;
		spotify_id?: string | null;
		spotify_popularity?: number | null;
		spotify_genres?: string[] | null;
		manual_genres?: string[] | null;
		aliases?: { alias_name: string }[];
	} | null; // Artist can be null for TBA sets
	stage: {
		id: string;
		name: string;
	} | null; // Stage can be null for lineup-only festivals
	start_time: string; // Keep original ISO string
	end_time: string; // Keep original ISO string
	youtube_url?: string | null; // YouTube URL for stream of this set
	soundcloud_url?: string | null; // SoundCloud URL for recording of this set
	apple_music_url?: string | null; // Apple Music URL for recording of this set
	alias_name?: string | null; // The alias name to display for this specific performance
	is_group_alias?: boolean; // Whether the alias_name refers to a group (e.g., "Everything Always") vs individual artist
	group_alias_slug?: string; // The slug to use for group routing when is_group_alias is true
	group_alias_name?: string | null; // The display name of the group alias (e.g., "Everything Always")
	member_artists?: Array<{
		artist_id: string;
		display_order: number;
		artist_name: string;
		artist_slug?: string;
		artist_image_url?: string | null;
		artist_spotify_id?: string | null;
		artist_spotify_genres?: string[] | null;
		artist_manual_genres?: string[] | null;
		artist_spotify_url?: string | null;
		artist_youtube_url?: string | null;
		artist_instagram?: string | null;
		artist_twitter?: string | null;
		artist_website_url?: string | null;
	}>; // Member artists for group aliases
	presents_name?: string | null; // Special presentation name (e.g., "Face II Face A/V" for themed sets)
	friends_set?: boolean; // Whether this is an "Artist & Friends" set
	is_collaborative?: boolean; // Whether this is a B2B/collaborative set
	display_format?: "B2B" | "x" | "+" | "F2F" | "&" | "vs" | "B3B" | "with"; // How to display the collaboration
	is_surprise?: boolean; // Whether this was originally TBA/mystery and should preserve surprise display
	placeholder_name?: string | null; // Display name for TBA/mystery slots (e.g., "?????", "TBA", "Special Guest")
	special_guests?: string | null; // Comma-separated list of special guests for this set
	event_type?: "main_festival" | "pre_party" | "after_party"; // Event type for same-venue pre/after parties
	day: string; // e.g., "friday", "saturday", "sunday"
	startHour: number; // Decimal hour (e.g., 13.5 for 1:30 PM)
	durationHours: number; // Duration in hours (e.g., 1.5)
	time: string; // Formatted time string (e.g., "11:10 PM - 12:40 AM")
	// For B2B sets: array of artists in order
	set_artists?: SetArtist[];
	// Program set data (for converted program sets)
	_program_set?: ProgramSetData;
	// Lineup-only indicator (no schedule/time/stage data)
	isLineupOnly?: boolean;
}

/**
 * Utility function to get the display name for a set.
 * For TBA/surprise sets, shows placeholder (e.g., "?????") to preserve mystery in most contexts
 * For collaborative sets, formats as "Artist1 B2B Artist2" or "Artist1 × Artist2"
 * For group aliases, formats as "Everything Always"
 * For presents sets, formats as "Artist pres. Special Set Name"
 * Uses the alias_name if specified, otherwise falls back to the artist name.
 */
export function getSetDisplayName(
	set: ProcessedSet | FetchedDbSet,
	context?: "review",
): string {
	// Handle program sets first (they have no artist)
	if (isProgramSet(set)) {
		return getProgramSetDisplayName(set);
	}

	// Helper function to get the appropriate fallback name for unknown artists
	const getUnknownArtistName = () => {
		return "?????";
	};

	// Handle TBA/surprise sets - show placeholder except in review context
	if (set.is_surprise && set.placeholder_name && context !== "review") {
		return set.placeholder_name;
	}
	// For B2B sets, construct the collaborative name
	if (set.is_collaborative && set.set_artists && set.set_artists.length > 1) {
		const connector =
			set.display_format === "x"
				? " × "
				: set.display_format === "+"
					? " + "
					: set.display_format === "F2F"
						? " F2F "
						: set.display_format === "vs"
							? " vs. "
							: set.display_format === "B3B"
								? " B3B "
								: set.display_format === "with"
									? " with "
									: " B2B ";
		const sortedArtists = set.set_artists.sort(
			(a, b) => a.position - b.position,
		);

		// Handle B2B group aliases (like "Everything Always")
		if (set.alias_name && set.is_group_alias) {
			if (set.presents_name) {
				return `${set.alias_name} pres. ${set.presents_name}`; // Simplified to "pres."
			}
			return set.alias_name;
		}

		const artistNames = sortedArtists.map((sa, index) => {
			// For the primary artist (position 0), use alias if it exists
			if (index === 0 && set.alias_name) {
				return set.alias_name;
			}
			return sa.artist.name || getUnknownArtistName();
		});

		const collaborativeName = artistNames.join(connector);

		// Handle hybrid B2B + Presents case
		if (set.presents_name) {
			return `${collaborativeName} pres. ${set.presents_name}`; // Simplified to "pres."
		}

		return collaborativeName;
	}

	// For friends sets, construct the friends name
	if (set.friends_set) {
		const artistName = set.artist?.name || getUnknownArtistName();
		return `${artistName} & Friends`;
	}

	// For presents sets, construct the presentation name
	if (set.presents_name) {
		const artistName =
			set.alias_name || set.artist?.name || getUnknownArtistName();
		return `${artistName} pres. ${set.presents_name}`; // Simplified to "pres."
	}

	// For single artist sets, use existing logic
	if (set.alias_name) {
		return set.alias_name;
	}
	return set.artist?.name || getUnknownArtistName();
}

/**
 * Utility function to get display parts for sets with proper formatting.
 * Follows a clear decision tree: collaborative → group alias → individual artists.
 *
 * Returns an object with parts for individual styling:
 * - Single artist: { mainText: "Artist Name", connector: null, secondaryText: null }
 * - Individual B2B: { mainText: "Testpilot", connector: "×", secondaryText: "Zhu", realMainText: "Deadmau5" }
 * - Group alias: { mainText: "Everything Always", connector: null, secondaryText: null, realMainText: "Dom Dolla & John Summit" }
 * - Presents: { mainText: "Artist", connector: "pres.", secondaryText: "Special Set" }
 * - 3+ artists: Uses parts array for proper muted connector styling
 */
export function getB2BDisplayParts(
	set: ProcessedSet,
	context?: "review",
): {
	mainText: string;
	connector: string | null;
	secondaryText: string | null;
	realMainText?: string | null;
	isB2B: boolean;
	isPresents: boolean;
	isFriends: boolean;
	isSurprise: boolean;
	parts?: Array<{
		text: string;
		realText?: string;
		type: "artist" | "connector" | "presents";
	}>;
	specialGuests?: string | null;
} {
	// ===== DECISION TREE LOGIC =====

	// Helper function to get the appropriate fallback name for unknown artists
	const getUnknownArtistName = () => {
		return "?????";
	};

	// Handle program sets first (they have no artist but should show their name)
	if (isProgramSet(set)) {
		const displayName = getProgramSetDisplayName(set);
		return {
			mainText: displayName,
			connector: null,
			secondaryText: null,
			realMainText: null,
			isB2B: false,
			isPresents: false,
			isFriends: false,
			isSurprise: false,
			specialGuests: null, // Program sets don't have special guests
		};
	}

	// Handle TBA/surprise sets - show placeholder except in review context
	if (set.is_surprise && set.placeholder_name && context !== "review") {
		return {
			mainText: set.placeholder_name,
			connector: null,
			secondaryText: null,
			isB2B: false,
			isPresents: false,
			isFriends: false,
			isSurprise: true,
			specialGuests: null, // Don't reveal special guests for surprise sets
		};
	}

	// 🎯 CHECK FOR GROUP ALIASES FIRST (before collaborative check)
	// This ensures group aliases display correctly even if is_collaborative is false
	const groupAliasName = set.group_alias_name || set.alias_name;
	if (groupAliasName && set.is_group_alias) {
		// For group aliases, prefer member_artists if available, otherwise use set_artists
		const allArtists = [
			...(set.artist
				? [{ artist: set.artist, position: -1 }]
				: []),
			...(set.set_artists || []),
		]
			.reduce(
				(acc, current) => {
					if (!current.artist || !current.artist.id) {
						return acc;
					}
					const exists = acc.some((item) => item.artist?.id === current.artist.id);
					if (!exists) {
						acc.push(current);
					}
					return acc;
				},
				[] as { artist: { id: string; name: string }; position: number }[],
			)
			.sort((a, b) => a.position - b.position);

		const memberNames = set.member_artists && set.member_artists.length > 0
			? set.member_artists
				.sort((a, b) => a.display_order - b.display_order)
				.map((m) => m.artist_name)
				.join(" & ")
			: allArtists
				.map((sa) => sa.artist.name)
				.join(" & ");

		return {
			mainText: groupAliasName,
			connector: set.presents_name ? "pres." : null,
			secondaryText: set.presents_name || null,
			realMainText: memberNames,
			isB2B: true,
			isPresents: !!set.presents_name,
			isFriends: false,
			isSurprise: false,
			specialGuests: set.special_guests || null,
		};
	}

	// 1️⃣ IS COLLABORATIVE? (Y/N)
	if (!set.is_collaborative) {
		// 🎯 SINGLE ARTIST PATH

		// Check for friends sets
		if (set.friends_set) {
			const artistName = set.artist?.name || getUnknownArtistName();
			return {
				mainText: artistName,
				connector: "& Friends",
				secondaryText: null,
				isB2B: false,
				isPresents: false,
				isFriends: true,
				isSurprise: false,
				specialGuests: set.special_guests || null,
			};
		}

		// For presents sets, construct the presentation name
		if (set.presents_name) {
			const artistName =
				set.alias_name || set.artist?.name || getUnknownArtistName();
			return {
				mainText: artistName,
				connector: "pres.", // Simplified to "pres."
				secondaryText: set.presents_name,
				isB2B: false,
				isPresents: true,
				isFriends: false,
				isSurprise: false,
				specialGuests: set.special_guests || null,
			};
		}

		// For single artist sets, check if special guests should be part of the name
		const artistName =
			set.alias_name || set.artist?.name || getUnknownArtistName();

		// Check if special guests should be shown as "w/" (for Symphony, orchestras, etc.)
		if (set.special_guests && !set.special_guests.includes(",")) {
			// Single special guest - check if it's a collaborative entity like Symphony
			const guest = set.special_guests.trim();
			const isCollaborativeGuest =
				guest.toLowerCase().includes("symphony") ||
				guest.toLowerCase().includes("orchestra") ||
				guest.toLowerCase().includes("chorus") ||
				guest.toLowerCase().includes("choir") ||
				guest.toLowerCase().includes("ensemble");

			if (isCollaborativeGuest) {
				return {
					mainText: artistName,
					connector: "w/",
					secondaryText: guest,
					isB2B: false,
					isPresents: false,
					isFriends: false,
					isSurprise: false,
					specialGuests: null, // Don't show as badge since it's in the name
				};
			}
		}

		// Regular single artist set
		if (set.alias_name) {
			return {
				mainText: set.alias_name,
				connector: null,
				secondaryText: null,
				isB2B: false,
				isPresents: false,
				isFriends: false,
				isSurprise: false,
				specialGuests: set.special_guests || null,
			};
		}
		return {
			mainText: artistName,
			connector: null,
			secondaryText: null,
			isB2B: false,
			isPresents: false,
			isFriends: false,
			isSurprise: false,
			specialGuests: set.special_guests || null,
		};
	}

	// ✅ COLLABORATIVE = TRUE, continue...

	const allArtists = [
		...(set.artist
			? [{ artist: set.artist, position: -1, alias_name: null }]
			: []), // Ensure primary artist is included
		...(set.set_artists || []),
	]
		.reduce(
			(acc, current) => {
				// Safety check: ensure artist and artist.id exist
				if (!current.artist || !current.artist.id) {
					return acc;
				}
				const exists = acc.some((item) => item.artist?.id === current.artist.id);
				if (!exists) {
					acc.push(current);
				}
				return acc;
			},
			[] as {
				artist: { id: string; name: string };
				position: number;
				alias_name?: string | null;
			}[],
		)
		.sort((a, b) => a.position - b.position);

	const sortedArtists = allArtists;
	const connector =
		set.display_format === "B2B"
			? "B2B"
			: set.display_format === "&"
				? "&"
				: set.display_format === "x"
					? "×"
					: set.display_format === "+"
						? "+"
						: set.display_format === "vs"
							? "vs."
							: set.display_format === "F2F"
								? "F2F"
								: set.display_format === "B3B"
									? "B3B"
									: set.display_format === "with"
										? "with"
										: "B2B"; // Default fallback

	// Note: Group aliases are now handled earlier in the function (before collaborative check)
	// This ensures they display correctly regardless of is_collaborative value

	// Handle 3+ artist collaborations
	if (sortedArtists.length > 2) {
		const parts: Array<{
			text: string;
			realText?: string;
			type: "artist" | "connector" | "presents";
		}> = [];
		for (let index = 0; index < sortedArtists.length; index++) {
			const sa = sortedArtists[index];
			if (!sa) continue;
			const artistPart = {
				text: sa.alias_name // Use artist-specific alias if available
					? sa.alias_name
					: index === 0 && set.alias_name && !set.is_group_alias
						? set.alias_name
						: sa.artist.name || getUnknownArtistName(),
				realText: sa.artist.name || getUnknownArtistName(),
				type: "artist" as const,
			};
			parts.push(artistPart);
			if (index < sortedArtists.length - 1) {
				parts.push({ text: connector, type: "connector" as const });
			}
		}

		return {
			mainText: "", // Not used for parts array
			connector: null,
			secondaryText: null,
			isB2B: true,
			isPresents: false,
			isFriends: false,
			isSurprise: false,
			parts,
			specialGuests: set.special_guests || null,
		};
	}

	// Handle 2-artist collaborations
	const mainArtist = sortedArtists[0];
	const secondaryArtist = sortedArtists[1];
	const mainText = mainArtist?.alias_name // Use artist-specific alias if available
		? mainArtist.alias_name
		: set.alias_name && !set.is_group_alias
			? set.alias_name
			: mainArtist?.artist.name || getUnknownArtistName();
	const realMainText = mainArtist?.artist.name || getUnknownArtistName();

	const secondaryText = secondaryArtist?.alias_name // Use artist-specific alias if available
		? secondaryArtist.alias_name
		: secondaryArtist?.artist.name || getUnknownArtistName();

	return {
		mainText,
		connector,
		secondaryText,
		realMainText: mainText !== realMainText ? realMainText : null,
		isB2B: true,
		isPresents: false,
		isFriends: false,
		isSurprise: false,
		specialGuests: set.special_guests || null,
	};
}

/**
 * Utility function to get all artist IDs for a set (used for search and filtering)
 */
export function getSetArtistIds(set: ProcessedSet | FetchedDbSet): string[] {
	if (set.is_collaborative && set.set_artists) {
		return set.set_artists.map((sa) => sa.artist_id);
	}
	// Handle TBA sets where artist is null
	if (!set.artist) {
		return [];
	}
	return [set.artist.id];
}

/**
 * Utility function to check if a set contains a specific artist
 */
export function setContainsArtist(
	set: ProcessedSet | FetchedDbSet,
	artistId: string,
): boolean {
	// TBA sets don't contain any specific artist
	if (set.is_surprise && !set.artist) {
		return false;
	}
	return getSetArtistIds(set).indexOf(artistId) !== -1;
}

/**
 * Utility function to check if a set has special guests
 */
export function setHasSpecialGuests(set: ProcessedSet | FetchedDbSet): boolean {
	return !!(set.special_guests && set.special_guests.trim().length > 0);
}

/**
 * Utility function to format special guests for display
 * Takes a comma-separated string and returns a cleaned, formatted version
 */
export function formatSpecialGuestsDisplay(
	guests: string | null,
): string | null {
	if (!guests || guests.trim().length === 0) {
		return null;
	}

	// Split by comma, trim whitespace, and rejoin with proper spacing
	return guests
		.split(",")
		.map((guest) => guest.trim())
		.filter((guest) => guest.length > 0)
		.join(", ");
}

/**
 * Utility function to split special guests into individual names for badge display
 * Takes a comma-separated string and returns an array of individual guest names
 */
export function parseSpecialGuestsForBadges(guests: string | null): string[] {
	if (!guests || guests.trim().length === 0) {
		return [];
	}

	// Split by comma, trim whitespace, and filter out empty strings
	return guests
		.split(",")
		.map((guest) => guest.trim())
		.filter((guest) => guest.length > 0);
}

/**
 * Utility function to format a genre for display with proper capitalization.
 * Converts lowercase Spotify genres to proper display format.
 */
export function formatGenre(genre: string): string {
	if (!genre || genre.trim().length === 0) return genre;

	const words = genre.toLowerCase().split(" ");

	return words
		.map((word, index) => {
			// Special cases for common EDM abbreviations
			if (word === "edm") return "EDM";
			if (word === "uk") return "UK";
			if (word === "us") return "US";
			if (word === "r&b") return "R&B";
			if (word === "dnb") return "DnB";
			if (word === "d&b") return "D&B";

			// Words that should stay lowercase (except when first word)
			const lowercaseWords = [
				"and",
				"or",
				"of",
				"the",
				"in",
				"on",
				"at",
				"by",
				"for",
				"with",
			];
			if (index > 0 && lowercaseWords.indexOf(word) !== -1) {
				return word;
			}

			// Capitalize first letter of word
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(" ");
}

/**
 * Utility function to parse genres from Spotify array format.
 * Takes arrays of genres and formats them for display.
 * Handles null/empty arrays gracefully and formats them for display.
 */
export function parseGenres(
	spotifyGenres: string[] | string | null,
	manualGenres?: string[] | string | null,
): string[] {
	let allGenres: string[] = [];

	// Handle spotify_genres (array format)
	if (Array.isArray(spotifyGenres)) {
		allGenres = [...spotifyGenres];
	} else if (
		typeof spotifyGenres === "string" &&
		spotifyGenres.trim().length > 0
	) {
		// Handle legacy string format for spotify_genres (just in case)
		allGenres = spotifyGenres.split("/").map((genre) => genre.trim());
	}

	// Add manual genres if they exist (now also array format)
	if (Array.isArray(manualGenres)) {
		for (const genre of manualGenres) {
			if (genre && genre.trim().length > 0 && allGenres.indexOf(genre) === -1) {
				allGenres.push(genre);
			}
		}
	} else if (
		typeof manualGenres === "string" &&
		manualGenres.trim().length > 0
	) {
		// Handle legacy string format for manual_genres (backward compatibility)
		const manualList = manualGenres.split(",").map((genre) => genre.trim());
		for (const genre of manualList) {
			if (allGenres.indexOf(genre) === -1) {
				allGenres.push(genre);
			}
		}
	}

	if (allGenres.length > 0) {
		return allGenres
			.map((genre) => formatGenre(genre))
			.filter((genre) => genre.length > 0)
			.slice(0, 3); // Limit to 3 genres max
	}

	return [];
}

/**
 * Utility function to check if a set matches a search term in any of its names or guests
 * Used for festival search functionality to include guest artists in results
 */
export function setMatchesSearch(
	set: ProcessedSet | FetchedDbSet,
	searchTerm: string,
	matchesSearchFn: (text: string, searchTerm: string) => boolean,
): boolean {
	// Check primary artist name
	if (set.artist?.name && matchesSearchFn(set.artist.name, searchTerm)) {
		return true;
	}

	// Check set alias name
	if (set.alias_name && matchesSearchFn(set.alias_name, searchTerm)) {
		return true;
	}

	// Check primary artist aliases
	if (
		set.artist?.aliases &&
		Array.isArray(set.artist.aliases) &&
		set.artist.aliases.length > 0
	) {
		const aliasMatch = set.artist.aliases.some(
			(alias: { alias_name: string }) => {
				return (
					alias &&
					typeof alias === "object" &&
					alias.alias_name &&
					typeof alias.alias_name === "string" &&
					matchesSearchFn(alias.alias_name, searchTerm)
				);
			},
		);
		if (aliasMatch) return true;
	}

	// Check B2B collaborators
	if (
		set.is_collaborative &&
		set.set_artists &&
		Array.isArray(set.set_artists)
	) {
		const b2bMatch = set.set_artists.some((setArtist) => {
			if (!setArtist || !setArtist.artist) return false;

			// Check collaborator's primary name
			if (
				setArtist.artist.name &&
				matchesSearchFn(setArtist.artist.name, searchTerm)
			) {
				return true;
			}

			// Check collaborator's aliases
			if (
				setArtist.artist.aliases &&
				Array.isArray(setArtist.artist.aliases) &&
				setArtist.artist.aliases.length > 0
			) {
				return setArtist.artist.aliases.some(
					(alias: { alias_name: string }) => {
						return (
							alias &&
							typeof alias === "object" &&
							alias.alias_name &&
							typeof alias.alias_name === "string" &&
							matchesSearchFn(alias.alias_name, searchTerm)
						);
					},
				);
			}

			return false;
		});
		if (b2bMatch) return true;
	}

	// Check special guests
	if (setHasSpecialGuests(set)) {
		const guestNames = parseSpecialGuestsForBadges(set.special_guests || null);
		const guestMatch = guestNames.some((guestName) =>
			matchesSearchFn(guestName, searchTerm),
		);
		if (guestMatch) return true;
	}

	return false;
}

/**
 * Check if a set is actually a program set (has _program_set data and no artist)
 */

export function isProgramSet(
	set: ProcessedSet | FetchedDbSet,
): set is (ProcessedSet | FetchedDbSet) & { _program_set: ProgramSetData } {
	const result =
		!set.artist &&
		"_program_set" in set &&
		Boolean((set as ProcessedSet)._program_set);
	return result;
}

/**
 * Get display name for program sets
 */
export function getProgramSetDisplayName(
	set: ProcessedSet | FetchedDbSet,
): string {
	if (isProgramSet(set)) {
		return set._program_set.name;
	}
	return "Unknown Program";
}

/**
 * Convert a program set to a ProcessedSet format for unified display
 * This allows program sets to appear alongside artist sets in existing components
 */
export function convertProgramSetToProcessedSet(
	programSet: FetchedDbProgramSet | ProcessedProgramSet,
	festival: {
		start_date: string;
		schedule_start_hour?: number;
		schedule_overnight?: boolean;
	},
): ProcessedSet {
	// Calculate day, time, and duration (similar to processDbSets logic)
	const startTime = new Date(programSet.start_time);
	const endTime = new Date(programSet.end_time);
	const durationMs = endTime.getTime() - startTime.getTime();
	const durationHours = durationMs / (1000 * 60 * 60);

	// Calculate start hour as decimal (e.g., 14.5 for 2:30 PM)
	const startHour = startTime.getHours() + startTime.getMinutes() / 60;

	// Calculate which festival day this is
	const festivalStart = new Date(festival.start_date);
	const daysDiff = Math.floor(
		(startTime.getTime() - festivalStart.getTime()) / (1000 * 60 * 60 * 24),
	);
	const dayNames = [
		"thursday",
		"friday",
		"saturday",
		"sunday",
		"monday",
		"tuesday",
		"wednesday",
	];
	const day =
		dayNames[Math.max(0, Math.min(daysDiff, dayNames.length - 1))] || "friday";

	// Format time string (e.g., "3:00 PM - 4:00 PM")
	const timeFormat = new Intl.DateTimeFormat("en-US", {
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
	const time = `${timeFormat.format(startTime)} - ${timeFormat.format(endTime)}`;

	return {
		id: programSet.id,
		artist: null, // Program sets have no artist
		stage: programSet.stage || { id: "", name: "Festival Wide" },
		start_time: programSet.start_time,
		end_time: programSet.end_time,
		day,
		startHour,
		durationHours,
		time,
		// Program set specific data in _program_set field for identification
		_program_set: {
			name: programSet.name,
			program_type: programSet.program_type,
			registration_required: programSet.registration_required,
			description: programSet.description,
		},
	} as ProcessedSet & { _program_set: ProgramSetData };
}

/**
 * Program Set data structure for non-artist festival programming
 * (ceremonies, activities, experiences, announcements)
 */
export interface FetchedDbProgramSet {
	id: string;
	name: string;
	description: string | null;
	program_type:
		| "ceremony"
		| "activity"
		| "experience"
		| "announcement"
		| "infrastructure";
	start_time: string; // ISO 8601 timestamp string
	end_time: string; // ISO 8601 timestamp string
	registration_required: boolean;
	image_url: string | null;
	youtube_url: string | null;
	stage: {
		id: string;
		name: string;
	} | null; // Can be null for festival-wide programs
}

/**
 * Processed Program Set for UI rendering
 */
export interface ProcessedProgramSet {
	id: string;
	name: string;
	description: string | null;
	program_type:
		| "ceremony"
		| "activity"
		| "experience"
		| "announcement"
		| "infrastructure";
	start_time: string; // Keep original ISO string
	end_time: string; // Keep original ISO string
	registration_required: boolean;
	image_url: string | null;
	youtube_url: string | null;
	stage: {
		id: string;
		name: string;
	} | null;
	day: string; // e.g., "friday", "saturday", "sunday"
	startHour: number; // Decimal hour (e.g., 13.5 for 1:30 PM)
	durationHours: number; // Duration in hours (e.g., 1.5)
	time: string; // Formatted time string (e.g., "11:10 PM - 12:40 AM")
}

/**
 * Unified schedule item that can be either an artist set or program set
 */
export type ScheduleItem =
	| { type: "artist_set"; data: ProcessedSet }
	| { type: "program_set"; data: ProcessedProgramSet };

/**
 * Raw response from get_festival_schedule RPC function
 */
export interface FestivalScheduleRpcResponse {
	item_type: "artist_set" | "program_set";
	item_id: string;
	name: string;
	start_time: string;
	end_time: string;
	stage_name: string | null;
	stage_id: string | null;
	// Artist set specific fields
	artist_name: string | null;
	artist_id: string | null;
	artist_image_url: string | null;
	artist_genres: string[] | null;
	is_collaborative: boolean | null;
	is_surprise: boolean | null;
	alias_name: string | null;
	presents_name: string | null;
	// Program set specific fields
	program_type: string | null;
	registration_required: boolean | null;
	// User interaction fields
	is_in_user_schedule: boolean | null;
	user_rating: number | null;
	user_loved: boolean | null;
	user_want_to_see: boolean | null;
	user_must_see: boolean | null;
	user_saw_live: boolean | null;
	user_saw_stream: boolean | null;
}
